import { z } from 'zod';
import { CrmListValueSchema } from './crm.js';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * Quality Management contracts (FRD §18, FRD-QLT-001 to QLT-008; CLR-43): quality issues, their
 * corrective and preventive actions, and verification and closure.
 *
 * The Quality Issue number (`code`) is generated by the system from its configured series
 * (FRD-QLT-003): in every response and in no request. Category and severity carry the value code
 * of a configurable list (SDD §15.3); the values are business data and both are optional.
 */
const TitleSchema = z.string().trim().min(1).max(200);
const LongTextSchema = z.string().trim().min(1).max(4000);
const ActionTextSchema = z.string().trim().min(1).max(2000);
const ListCodeValueSchema = z.string().trim().min(1).max(50);
export const QualityIssueStatusSchema = z.enum(['open', 'closed']);
export const QualityActionTypeSchema = z.enum(['corrective', 'preventive']);
export const QualityActionStatusSchema = z.enum(['open', 'completed']);
export const QualityActionSchema = z.object({
    id: IdSchema,
    issueId: IdSchema,
    actionType: QualityActionTypeSchema,
    description: z.string(),
    responsibleEmployeeId: IdSchema,
    /** The employee's ID and name, as the HR module labels them. */
    responsibleLabel: z.string().nullable(),
    dueDate: BusinessDateSchema.nullable(),
    status: QualityActionStatusSchema,
    completedOn: BusinessDateSchema.nullable(),
    /** Open, with a due date before today's business date. */
    overdue: z.boolean(),
    /** For `If-Match` on a change (SDD §7.3). */
    version: z.number().int(),
});
/** The verification that closed an issue (FRD-QLT-007). */
export const QualityVerificationSchema = z.object({
    outcome: z.string(),
    verifiedOn: BusinessDateSchema,
    /** The verifying user, for their photo beside the name. */
    verifiedBy: IdSchema,
    /** The verifying user's display name. */
    verifiedByName: z.string().nullable(),
});
export const QualityIssueSchema = z.object({
    id: IdSchema,
    /** The generated Quality Issue number (FRD-QLT-003). */
    code: z.string(),
    title: z.string(),
    description: z.string(),
    identifiedOn: BusinessDateSchema,
    raisedByEmployeeId: IdSchema,
    raisedByLabel: z.string().nullable(),
    ownerEmployeeId: IdSchema,
    ownerLabel: z.string().nullable(),
    categoryCode: z.string().nullable(),
    severityCode: z.string().nullable(),
    rootCause: z.string().nullable(),
    status: QualityIssueStatusSchema,
    openActionCount: z.number().int(),
    actionCount: z.number().int(),
    version: z.number().int(),
});
/** One issue with its actions and, once closed, its verification. */
export const QualityIssueDetailSchema = QualityIssueSchema.extend({
    actions: z.array(QualityActionSchema),
    verification: QualityVerificationSchema.nullable(),
});
export const CreateQualityIssueRequestSchema = z.strictObject({
    title: TitleSchema,
    description: LongTextSchema,
    identifiedOn: BusinessDateSchema,
    raisedByEmployeeId: IdSchema,
    ownerEmployeeId: IdSchema,
    categoryCode: ListCodeValueSchema.optional(),
    severityCode: ListCodeValueSchema.optional(),
    rootCause: LongTextSchema.optional(),
});
/** A partial change to an open issue; `null` clears an optional field. */
export const UpdateQualityIssueRequestSchema = z.strictObject({
    title: TitleSchema.optional(),
    description: LongTextSchema.optional(),
    identifiedOn: BusinessDateSchema.optional(),
    ownerEmployeeId: IdSchema.optional(),
    categoryCode: ListCodeValueSchema.nullable().optional(),
    severityCode: ListCodeValueSchema.nullable().optional(),
    rootCause: LongTextSchema.nullable().optional(),
});
export const CreateQualityActionRequestSchema = z.strictObject({
    actionType: QualityActionTypeSchema,
    description: ActionTextSchema,
    responsibleEmployeeId: IdSchema,
    dueDate: BusinessDateSchema.optional(),
});
/** A partial change to an action; `completed` records or withdraws its completion. */
export const UpdateQualityActionRequestSchema = z.strictObject({
    actionType: QualityActionTypeSchema.optional(),
    description: ActionTextSchema.optional(),
    responsibleEmployeeId: IdSchema.optional(),
    dueDate: BusinessDateSchema.nullable().optional(),
    completed: z.boolean().optional(),
});
/** Verify and close (FRD-QLT-007): the outcome that was verified. */
export const CloseQualityIssueRequestSchema = z.strictObject({
    outcome: LongTextSchema,
});
/** The category and severity lists, for the pickers. */
export const QualityIssueListsSchema = z.object({
    category: z.array(CrmListValueSchema),
    severity: z.array(CrmListValueSchema),
});
//# sourceMappingURL=quality.js.map